import React, { useEffect, useState, useMemo, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";
import {isMobile} from 'react-device-detect';

import Viewport from "../components/Viewport";
import LayerList from "../components/LayerList";
import OtherUser from "../components/OtherUser";

import { useObjectStore } from "../zustand/objects";
import { useAccountStore } from "../zustand/account";

import ArtboardPanel from "../components/ArtboardPanel";
import TopProjectAppBar from "../components/TopProjectAppBar";
import TextModal from "../components/TextModal";
import CommentSidebar from "../components/CommentSidebar";

import { Grid2, Box } from "@mui/material/";
import TurnLeftIcon from '@mui/icons-material/TurnLeft';
import { Tooltip, AppShell, Divider, Tabs, Text, AppShellAside, AppShellSection, Badge, LoadingOverlay, Group, Button, Dialog, Stack, Avatar, useMantineTheme  } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import {editor} from "../styleFile.js"

import debounce from 'lodash.debounce';

import { checkTutorialSeen, updateTutorialSeen, postRealtimeToken, deleteRealtimeToken, getComments } from "../api";

function useQuery() {
  const { search } = useLocation();

  return useMemo(() => new URLSearchParams(search), [search]);
} 

const DialogueDocs = () => {
  const {helpOpen, setHelpOpen} = useAccountStore()
  const [dialogueStatus, setDialogueStatus] = useState(1)

  const checkTutorialStatus = () => {
    checkTutorialSeen().then( res => setHelpOpen(!res.tutorialSeen.editor))
  }

  useEffect(() => {
    checkTutorialStatus()
  },[])

  const dialogueTitle = {
    1: '1/6: The canvas ',
    2: '2/6: Project level ',
    3: '3/6: Content level',
    4: '4/6: Share and preview',
    5: '5/6: Configure content',
    6: '6/6: Collaborating'
  }

  const dialogueDescription = {
    1: 'Click and drag to rotate your point of view. Do the same with shift held down to pan. Zoom using your mouse wheel or two fingers on your trackpad.',
    2: 'This is where you can change page, undo/redo, rename your project and trigger these instructions.',
    3: 'Use this menu space to add uploaded models, plain text, Figma frames, pre-made assets and templates here. Or generate 3D models with AI.',
    4: 'Here are options to preview your concept in first person, share preview links, toggle/leave comments and configure & export storyboards.',
    5: 'Use this sidebar to browse and edit objects, scenes and comments.',
    6: 'Here you can view who is online, bring them to your scene and observe what users can see in preview.'
  }
 
  const dialoguePosition = {
    1: {top:'75%', left:'1%'},
    2: {top:'6.5%', left:'1%'},
    3: {top:'6.5%', left:'40%'},
    4: {top:'6.5%', left:'71%'}, 
    5: {top:'30%', left:'54%'},
    6: {top:'75%', left:'54%'}
  }

  const resetStatus = () => {
    setDialogueStatus(1)
  }

  
  
  const closeClick = () => {
    setHelpOpen(false)
    setTimeout(resetStatus, 250);
    updateTutorialSeen({
      tutorialPage: "editor",
      tutorialSeen: true
    })
  }

  return (
    <Dialog 
    position={dialoguePosition[dialogueStatus]}
    opened={helpOpen} withCloseButton onClose={() => closeClick()} size="lg" radius="md">
      <Text size="sm" mb="xs" fw={500}>
        {dialogueTitle[dialogueStatus]}
      </Text>

      <Group align="flex-end">
      <Text size="xs" mb="xs" fw={400}>
        {dialogueDescription[dialogueStatus]}
      </Text>
        <Button color="black" disabled={dialogueStatus === 1} onClick={() => setDialogueStatus(dialogueStatus - 1) }>Back</Button>
        <Button color="black" disabled={dialogueStatus === 6} onClick={() => setDialogueStatus(dialogueStatus + 1)}>Next</Button>
      </Group>
    </Dialog>
  )
}


const EditorPage = () => {

  const renderCount = useRef(0);

  let query = useQuery();

  const queryString = query.get("artboard")

  const { projectKey } = useParams();
  
  const { realtimeToken, updateArtboardAndPreviewCamera, loadThreads, setViewedComments, viewedThread, loadComments, setRealtimeToken, currentObjectArtboard, presence, addOrUpdatePresence, removePresence, joinChannel, xanoClient, realtimeEventUpdate, screenshotStatus, tabValue, setTabValue, getSpecificUserProject, artboards, loadObjects, updateArtboard, undoing, selectedObjectID, unloadObjects, getAIModels, isOtherPreviewOpen } = useObjectStore();
  const {updateFirstName} = useAccountStore()

//XANO WEBSOCKETS

const [realtime, setRealtime] = useState(false)

const setRealtimeTokenFunction = (token) => {
  setRealtimeToken(token)
}

const processTokenResponse = (res) => {
  updateFirstName(res.firstName)
  setRealtimeTokenFunction(res.id)
  
}

  const handleJoinToken = () => {
    postRealtimeToken().then(res => processTokenResponse(res))
  }

  const isPageHidden = useRef(false);
 

  const handleVisibilityChange = () => {
    isPageHidden.current = document.visibilityState === 'hidden';
  };

 
const joinFunction = () => {
  setRealtime(true)
    xanoClient.setRealtimeAuthToken(localStorage.getItem('user'))
    handleJoinToken()
}

try {
// declare channel
const projectChannel = xanoClient.channel(`project/${projectKey}`, {
  presence:  true,
});
    // check for connection 
    projectChannel.on("connection_status", (action) => {
      if (action.payload.status === "connected") {
        joinFunction(action)
      }
    }) 

      // react to someone joining/leaving
      projectChannel.on("presence_update", (action) => {
        const members = projectChannel.getPresence()
        
        action.payload.action === "join" && addOrUpdatePresence(action.payload.presence.socketId, 'editor', action)  
        action.payload.action === "leave" && removePresence(action.payload.presence.socketId)
      })      

    // listen for messages
    realtime && projectChannel.on("message", (action) => {

      // getting realtimeToken here so it's always the freshest one and not undefined
      const realtimeToken = useObjectStore.getState().realtimeToken;
      
      if (action.options.type === "projectUpdate") {
        if (realtimeToken === action.payload.realtime_token) {
           
            if (action.payload.updateType === "commentDelete" || action.payload.updateType === "commentAdd") {
              viewedThread === action.payload.threads_id && debounce(getComments(action.payload.threads_id).then(res => setViewedComments(res)), 100)
            }
            else if (action.payload.updateType === "threadsDelete" || action.payload.updateType === "threadsAdd" || action.payload.updateType === "threadsUpdate") {
              debounce(loadThreads(), 100)
            }
            else if (action.payload.updateType === "objectAdd" || action.payload.updateType === "objectRemove") {
              debounce(realtimeEventUpdate(action), 100)
            }
            else if (action.payload.updateType === "objectUpdate" && action.payload.field !== "matrixState") {
              debounce(realtimeEventUpdate(action), 100)
            }
            else {
              return 
            }
        }  
        
       else if (realtimeToken !== action.payload.realtime_token) {
        debounce(realtimeEventUpdate(action), 100)}
      }
      else if (action.options.type === "presenceUpdate") {

        
            action.payload.function === "bringToMe" && updateArtboardAndPreviewCamera(action.payload.artboard)

            if (realtimeToken === action.payload.realtime_token && action.payload.field === "cursor") {
              return 
            }
            
            else {
              debounce(addOrUpdatePresence(action.client.socketId, action.payload.update[action.payload.field].page, action), 20)
            }
      }
    
    })

  } catch (err) {
    console.log("Error setting up Xano channel:", err); // Log channel setup errors
  }

  // have commented out unloadProject() and made getspecificUserProject() the async function
  // not 100% if this will cause issues. but seems to have stopped flickering of new proj. load.
  
  async function unloadProject() {
    unloadObjects()
  }

  useEffect(() => {
    unloadProject().then(() => 
    getSpecificUserProject(projectKey))
    .then(result => loadObjects())
    .then(res =>
      queryString ? updateArtboard(queryString) : updateArtboard('1'))
    // .then(() => realtimeFunctions())
  }, []);

  const [cameraDetails, setCameraDetails] = useState({
    id: "",
    position: []
  });




const [selectedTab, setSelectedTab] = useState('objects')

const handleTabClick = (e) => setSelectedTab(e)

const [mobileOpened, { toggle: toggleMobile }] = useDisclosure();
const [desktopOpened, { toggle: toggleDesktop }] = useDisclosure(true);

const [mobileAsideOpened, { toggle: toggleMobileAside }] = useDisclosure();
const [desktopAsideOpened, { toggle: toggleDesktopAside }] = useDisclosure(true);

useEffect(() => {
  selectedObjectID !== '0' &&
  selectedTab === '' &&
  handleTabClick('objects')

}, [selectedObjectID]);

  const theme = useMantineTheme();


  return ( 
    <>
    <AppShell
      header={{ height: 60 }}
      // navbar={{ breakpoint: 'sm', collapsed: { mobile: !mobileOpened, desktop: !desktopOpened } }}
      aside={{   width: 275, breakpoint: 'md', collapsed: { mobile: !mobileAsideOpened, desktop: !desktopAsideOpened } }}

    >
      
      <LoadingOverlay loaderProps={{ children: <h5>Please wait a few seconds, capturing screenshots...</h5>, color: 'black' }} visible={screenshotStatus} zIndex={1200} overlayProps={{ radius: "sm", blur: 2 }} />
      <DialogueDocs/>
      <Box sx={{ flexGrow: 1, overflow: "hidden" }}
        >
        <TextModal />
        <Grid2 container style={{ overflow: "hidden" }}>
          <AppShell.Header >
            <TopProjectAppBar mode={"editor"} projectKey={projectKey} />
          </AppShell.Header>
          <AppShell.Main p={0}>
          
          <Grid2 item xs={12}>
           <Viewport
              undoing={undoing}
              artboards={artboards} 
              projectKey={projectKey}
              presence={presence}
            />
          </Grid2>
          </AppShell.Main>
         
         
        </Grid2>
      </Box>
      <AppShellAside>
        {/* Show when artboards isn't null */}
        {artboards && currentObjectArtboard && <Tabs
        style= {{
                height: Object.keys(presence).length > 0 ? 525 : 675, 
                // Target the 'height' property for a smooth transition
                transition: 'height 200ms ease-in-out', 
                // You might also need 'max-height' if using that for collapsing
                // transition: 'max-height 300ms ease-in-out', 
            }}
         value={tabValue} onChange={(val) => setTabValue(val)} pt={10}> 
      <Tabs.List pl={10} pr={10} mb={0}>
        <Tabs.Tab p={5} value="elements">
          <Text fw={700} size="medium">
          Elements</Text>
        </Tabs.Tab>
        <Tabs.Tab p={5} value="scenes">
          <Text fw={700} size="medium">
          Scenes</Text>
        </Tabs.Tab>
        <Tabs.Tab p={5} value="comments">
          <Text fw={700} size="medium">
                  Comments</Text>
              </Tabs.Tab>
            </Tabs.List>

            {/* MODEL CONTENT */}
            <Tabs.Panel value="elements" h={'100%'}>
              <AppShellSection h={'95%'} style={editor.sidebarSection} pl={10} pr={10} pt={10}>
                <LayerList mobile={true} />
              </AppShellSection>
            </Tabs.Panel>

            <div style={editor.modelAccordion}>
              <Button onClick={toggleDesktopAside} variant="default" size="xs" style={editor.sidebarToggleButton}>
                {desktopAsideOpened ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
              </Button>

            </div>
            {!desktopAsideOpened && <div
              style={editor.sidebarAccordion} />}

            {/* SCENE CONTENT */}
            <Tabs.Panel value="scenes" h={'100%'}>
              <AppShellSection h={'95%'} style={editor.sidebarSection} pl={10} pr={10} pt={10}>
                {/* <AppShellSection style={editor.sidebarSection}> */}
                <ArtboardPanel projectKey={projectKey} mobile={true} />
              </AppShellSection>
            </Tabs.Panel>

      {/* COMMENT CONTENT */}     
      <Tabs.Panel value="comments"  h={'100%'} >
      <AppShellSection h={'95%'}  style={editor.sidebarSection} pl={10} pr={10} pt={10}>
      {/* <AppShellSection h={'55vh'} style={editor.sidebarSection}> */}
      {/* <AppShellSection style={editor.sidebarSection}> */}
      <CommentSidebar /> 
      </AppShellSection>
      </Tabs.Panel>
      </Tabs>}
 
          {/* OTHERS – want this to expand contract as it gets taller and overlap the Scene Content section above */}
          <AppShellSection h={'100%'} mah={185}>
            <Box
              // Use Mantine's 'sx' prop for inline styling
              sx={{
                paddingTop: 0,
                height:'100%',
                // Your new border and rounding styles
                borderTop: `2px solid ${theme.colors.secondary[3]}`,
                // borderTopLeftRadius: '10px', // Example rounding
                // borderTopRightRadius: '10px',
                backgroundColor:'white'
              }}
            >
              <Others presence={presence} />
            </Box>
          </AppShellSection>
        </AppShellAside>
    </AppShell >
    </>
  )
}

export default EditorPage;



const Others = ({ presence }) => {
  const [open, setOpen] = useState(false)
  const [hover, setHover] = useState(false)
  const { currentObjectArtboard, bringToArtboard, sendPresenceUpdateToChannel, projectKey } = useObjectStore();
  const { firstName, isEditor } = useAccountStore()

  const updateObject = {
      updateType: "presenceUpdate",
      function: "bringToMe",
      projectKey: projectKey,
      artboard: currentObjectArtboard,
      name: firstName 
    }
  
    const handleBring = () => sendPresenceUpdateToChannel(updateObject)

  const handleOpenClick = (e) => {
    e.preventDefault()
    setOpen(!open)
  }

const [modalOpen, setModalOpen] = useState(true) 
 
  const theme = useMantineTheme();

  return (

    <Stack h={'100%'} pb={'10px'} pl={'15px'} pr={'15px'} gap={7.5} justify="space-between">
      <Stack gap={10} h={'80%'}>
        <Group justify="space-between" h={'20%'}>
          <Text fw={1000} size="xs" c="dimmed" my="sm">
            USERS</Text><Badge size="sm" variant="light" color={Object.keys(presence).length > 0 ? 'amber' : 'gray'}>
              {Object.keys(presence).length ? Object.keys(presence).length : 0} ONLINE</Badge>
        </Group>
       {Object.keys(presence).length > 0 && <Stack pt={5} pb={5} pl={10} pr={10} h={'80%'} gap={5} style={{ ...editor.sidebarOthersUsers, backgroundColor: theme.colors.neutral[0] }}>
          {Object.keys(presence).map((mapped, i) => (
            <OtherUser
              open={open}
              key={i}
              socketId={mapped}
              update={presence[mapped].update}
              isMobile={isMobile} />
          ))}
        </Stack>}
        </Stack>
      {Object.keys(presence).length > 0 && <Stack h={'20%'}>
      <Button  variant="default" size="xs" onClick={() => handleBring()}>
        Bring all to me</Button></Stack>}
    </Stack>



  )
}


